import { selectActiveProgramPackage } from '@quickmd-llc/frontend-utils'
import { useGetPatientsPatientIdPackages } from '@/api/generated/package/package'
import { type PatientPackageApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { selectProgramVisitBadgeFlag } from '@/store/flags/flagsSelectors'

/**
 * The plan the patient chart header's chip speaks for, or `null` when there is
 * nothing to speak for — because the patient holds no usable plan, or the kill
 * switch is off.
 *
 * Reads `GET /api/v1/patients/{patientId}/packages` rather than deriving from
 * the chart's visit lists, which are the only other source of coverage data and
 * are fetched lazily when staff expand a section: a header chip built on those
 * would be silently absent until someone clicked. The purchase is also the only
 * place the plan's own lifecycle lives — an expired plan is indistinguishable
 * from a live one on a visit's `Coverage` block.
 *
 * The flag gates the request itself, not just the chip, so a chart loaded with
 * the switch off makes no call at all. The patient id gates it for the same
 * reason: the chart renders its header before the patient resolves, and an
 * ungated call would spend that first render requesting `/patients//packages`.
 *
 * `selectActiveProgramPackage` is generic, so what comes back here is admin's
 * own generated `PatientPackageApiOutContract` rather than the shared structural
 * stand-in the helper accepts.
 */
export function useProgramPlanPackage(patientId: string | null | undefined): PatientPackageApiOutContract | null {
  const isProgramBadgeEnabled = useAppSelector(selectProgramVisitBadgeFlag)
  const { data: packages } = useGetPatientsPatientIdPackages(patientId ?? '', {
    query: { enabled: isProgramBadgeEnabled && Boolean(patientId) },
  })

  if (!isProgramBadgeEnabled) {
    return null
  }

  return selectActiveProgramPackage(packages)
}
